import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const SALARY_STATUS = [
  { value: "waiting_accountant", label: "Könyvelőre vár", tone: "amber" },
  { value: "waiting_payment", label: "Kifizetésre vár", tone: "blue" },
  { value: "paid", label: "Kifizetve", tone: "emerald" },
  { value: "draft", label: "Tervezet", tone: "muted" },
  { value: "approved", label: "Jóváhagyva", tone: "blue" },
];

export const SALARY_STATUS_TONES = {
  emerald:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-400",
  blue:
    "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-400",
  amber:
    "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-400",
  muted: "border-border bg-muted text-muted-foreground",
};

const SALARY_DOT_TONES = {
  emerald: "bg-emerald-500",
  blue: "bg-blue-500",
  amber: "bg-amber-500",
  muted: "bg-muted-foreground/60",
};

export function salaryStatusMeta(value) {
  return SALARY_STATUS.find((s) => s.value === value) || SALARY_STATUS[0];
}

export function SalaryStatusBadge({ value }) {
  const meta = salaryStatusMeta(value);
  return (
    <span
      data-testid={`salary-status-${value}`}
      className={cn(
        "inline-flex h-5 items-center gap-1.5 rounded-full border px-2 text-[11px] font-medium",
        SALARY_STATUS_TONES[meta.tone]
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", SALARY_DOT_TONES[meta.tone])} />
      {meta.label}
    </span>
  );
}

const emptyForm = {
  pay_month: "",
  status: "waiting_accountant",
  gross_salary: "",
  hours_worked: "",
  notes: "",
};

// Selectable pay months: only past months, max 2 months back.
// E.g. if today is 2026-02-* -> options: ["2026-01", "2025-12"]. Default = previous month.
const payMonthOptions = () => {
  const d = new Date();
  const opts = [];
  for (let back = 1; back <= 2; back += 1) {
    const dt = new Date(d.getFullYear(), d.getMonth() - back, 1);
    opts.push(
      `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`
    );
  }
  return opts; // newest first
};

const defaultPayMonth = () => payMonthOptions()[0];

const numberOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function SalaryFormSheet({
  open,
  onOpenChange,
  employee,
  company,
  site,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [pendingBonus, setPendingBonus] = useState({ count: 0, sum: 0 });
  const [pendingCommission, setPendingCommission] = useState({
    count: 0,
    sum: 0,
  });

  // Reset on open — this sheet is create-only. Also fetch pending bonuses + commissions.
  useEffect(() => {
    if (!open) return;
    setForm({
      ...emptyForm,
      pay_month: defaultPayMonth(),
    });
    setErr("");
    setPendingBonus({ count: 0, sum: 0 });
    setPendingCommission({ count: 0, sum: 0 });
    if (employee?._id) {
      api
        .get(`/employees/${employee._id}/bonuses/pending-sum`)
        .then(({ data }) => setPendingBonus(data))
        .catch(() => {});
      api
        .get(`/employees/${employee._id}/commissions/pending-sum`)
        .then(({ data }) => setPendingCommission(data))
        .catch(() => {});
    }
  }, [open, employee?._id]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      if (!form.pay_month || !/^\d{4}-\d{2}$/.test(form.pay_month)) {
        setErr("A tárgyhó megadása kötelező (YYYY-MM).");
        setSaving(false);
        return;
      }
      if (!employee?._id) throw new Error("Hiányzó munkavállaló.");
      const payload = {
        pay_month: form.pay_month,
        status: form.status,
        gross_salary: numberOrNull(form.gross_salary),
        hours_worked: numberOrNull(form.hours_worked),
        notes: form.notes || null,
      };
      const { data } = await api.post(
        `/employees/${employee._id}/salaries`,
        payload
      );
      toast.success("Bér rögzítve.");
      onOpenChange(false);
      if (onSaved) onSaved(data);
    } catch (error) {
      setErr(formatApiError(error?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-xl"
        data-testid="salary-form-sheet"
      >
        <SheetHeader className="border-b border-border px-6 py-5">
          <SheetTitle>Új bér rögzítése</SheetTitle>
          <SheetDescription>
            Rögzíts új munkabér bejegyzést a munkavállalóhoz. A nettó értékeket
            és a bérjegyzéket a Könyvelés lépésnél töltheted ki.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-7 overflow-y-auto px-6 py-6">
            {/* Employee mini section */}
            {employee && (
              <section
                data-testid="salary-form-employee-section"
                className="rounded-xl border border-border bg-muted/30 p-4"
              >
                <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Munkavállaló
                </p>
                <p className="mt-1 text-sm font-medium">
                  {employee.titulus ? `${employee.titulus} ` : ""}
                  {`${employee.last_name || ""} ${employee.first_name || ""}`.trim() ||
                    "—"}
                </p>
                {employee.position && (
                  <p className="text-[12.5px] text-muted-foreground">
                    {employee.position}
                  </p>
                )}
                {employee.email && (
                  <p className="font-mono text-[11.5px] text-muted-foreground">
                    {employee.email}
                  </p>
                )}
              </section>
            )}

            <Section title="Időszak">
              <Grid cols={2}>
                <Field label="Tárgyhó *" testId="salary-form-pay-month">
                  <Select
                    value={form.pay_month}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, pay_month: v }))
                    }
                  >
                    <SelectTrigger data-testid="salary-form-pay-month-trigger">
                      <SelectValue placeholder="Válassz hónapot" />
                    </SelectTrigger>
                    <SelectContent>
                      {payMonthOptions().map((m) => (
                        <SelectItem
                          key={m}
                          value={m}
                          data-testid={`salary-form-pay-month-option-${m}`}
                        >
                          {formatPayMonth(m)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <ReadonlyField
                  label="Munkáltató cég"
                  value={company?.name}
                  hint={
                    company?.type === "franchise" ? "franchise partner" : null
                  }
                  testId="salary-form-company-readonly"
                />
              </Grid>
              <ReadonlyField
                label="Telephely"
                value={site?.name}
                testId="salary-form-site-readonly"
              />
              <p
                data-testid="salary-form-inheritance-hint"
                className="text-[11px] text-muted-foreground"
              >
                A munkáltató cég és a telephely a munkavállaló profiljából
                öröklődik. Módosításhoz frissítsd a munkavállaló adatait. Az új
                bér automatikusan „Könyvelőre vár” státusszal kerül rögzítésre.
              </p>
            </Section>

            <Section title="Munkabér (bruttó)">
              <Field label="Bruttó bér (Ft)" testId="salary-form-gross-salary">
                <Input
                  type="number"
                  inputMode="numeric"
                  step="1"
                  min="0"
                  value={form.gross_salary}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, gross_salary: e.target.value }))
                  }
                  placeholder="0"
                  className="font-mono"
                />
              </Field>
              <Grid cols={2}>
                <AutoSumField
                  testId="salary-form-gross-commission"
                  label="Bruttó jutalék (automatikus)"
                  pending={pendingCommission}
                  emptyHint="Nincs jóváírható jutalék."
                  countHint={(n) =>
                    `${n} db jóváhagyott, még nem kifizetett jutalék összege.`
                  }
                />
                <AutoSumField
                  testId="salary-form-gross-bonus"
                  label="Bruttó bónusz (automatikus)"
                  pending={pendingBonus}
                  emptyHint="Nincs jóváírható bónusz."
                  countHint={(n) =>
                    `${n} db jóváhagyott, még nem kifizetett bónusz összege.`
                  }
                />
              </Grid>
            </Section>

            <Section title="Egyéb">
              <Field label="Összes ledolgozott óra" testId="salary-form-hours">
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  min="0"
                  value={form.hours_worked}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, hours_worked: e.target.value }))
                  }
                  placeholder="0"
                  className="font-mono"
                />
              </Field>
              <Field label="Megjegyzés" testId="salary-form-notes">
                <Textarea
                  value={form.notes}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, notes: e.target.value }))
                  }
                  rows={2}
                />
              </Field>
            </Section>

            {err && (
              <div
                data-testid="salary-form-error"
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
              >
                {err}
              </div>
            )}
          </div>

          <SheetFooter className="flex-row justify-end gap-2 border-t border-border bg-[hsl(var(--sipx-elevated))] px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-salary-form"
            >
              Mégse
            </Button>
            <Button type="submit" disabled={saving} data-testid="submit-salary-form">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : (
                "Rögzítés"
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

/* --- helpers --- */
function Section({ title, children }) {
  return (
    <section className="space-y-4">
      <header className="border-b border-border pb-2">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {title}
        </h3>
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Grid({ cols = 2, children }) {
  return (
    <div
      className={cn(
        "grid gap-3",
        cols === 2 && "grid-cols-1 sm:grid-cols-2"
      )}
    >
      {children}
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function ReadonlyField({ label, value, hint, testId }) {
  const isEmpty = !value;
  return (
    <div className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      <div
        className={cn(
          "flex h-9 items-center rounded-md border border-border bg-muted/40 px-3 text-sm",
          isEmpty && "text-muted-foreground/70"
        )}
      >
        <span className="truncate">{isEmpty ? "— Nincs hozzárendelve —" : value}</span>
        {hint && (
          <span className="ml-2 text-[11px] text-muted-foreground">· {hint}</span>
        )}
      </div>
    </div>
  );
}

function AutoSumField({ testId, label, pending, emptyHint, countHint }) {
  return (
    <div className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      <div
        data-testid={`${testId}-display`}
        className={cn(
          "flex h-9 items-center justify-end rounded-md border border-border bg-muted/40 px-3 font-mono text-sm",
          pending.sum === 0 && "text-muted-foreground/70"
        )}
      >
        {new Intl.NumberFormat("hu-HU", {
          maximumFractionDigits: 0,
        }).format(pending.sum || 0)}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {pending.count > 0 ? countHint(pending.count) : emptyHint}
      </p>
    </div>
  );
}

export function formatHuf(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("hu-HU", {
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatHours(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("hu-HU", {
    maximumFractionDigits: 1,
  }).format(n);
}

export function formatPayMonth(value) {
  if (!value) return "—";
  // value expected as YYYY-MM
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return value;
  const year = m[1];
  const month = parseInt(m[2], 10);
  const months = [
    "január",
    "február",
    "március",
    "április",
    "május",
    "június",
    "július",
    "augusztus",
    "szeptember",
    "október",
    "november",
    "december",
  ];
  return `${year}. ${months[month - 1] || m[2]}`;
}
